
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { User, Session, isAuthRetryableFetchError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { setSentryUser, clearSentryUser } from '@/lib/sentry';
import { flushAnalytics, identifyUser, resetAnalytics } from '@/lib/analytics';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  platformRole: 'platform_admin' | 'consultant' | 'client' | null;
  signIn: (email: string, password: string) => Promise<{
    error: Error | null;
    data: { user: User | null; session: Session | null };
  }>;
  signUp: (email: string, password: string) => Promise<{
    error: Error | null;
    data: { user: User | null; session: Session | null };
  }>;
  signInWithOAuth: (provider: 'azure' | 'google') => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Routes that manage their own auth/session state and must not be
// force-navigated to /auth — e.g. /accept-invite preserves a `?token=`
// query param that a redirect through /auth would drop.
const isPublicAuthRoute = (pathname: string) => pathname.startsWith("/accept-invite");

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [platformRole, setPlatformRole] = useState<'platform_admin' | 'consultant' | 'client' | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Ref so the auth listener can read current path without it being a dep.
  // Without this, location.pathname in the dep array re-runs the entire effect
  // (re-subscribing, calling getSession) on every navigation.
  const locationPathnameRef = useRef(location.pathname);
  useEffect(() => {
    locationPathnameRef.current = location.pathname;
  });

  useEffect(() => {
    // Set up authentication state listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log("Auth state changed:", { event, user: currentSession?.user });

        // Check for auth errors in the URL — but only on the app's own social-login OAuth
        // callback route (the bare root, matching config.toml's `site_url`/fallback redirect
        // Supabase's own OAuth flow lands on). Without this pathname guard, a real provider's
        // own error redirect elsewhere in the app (e.g. /erp/callback's Microsoft-consent-denial
        // redirect, which also carries error=access_denied) gets misread as OUR social-login
        // failing, forcibly navigating away before that route's own error UI ever renders
        // (issue #907).
        const urlParams = new URLSearchParams(window.location.search);
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');
        const isOwnOAuthCallback = locationPathnameRef.current === '/';

        // Handle OAuth errors before setting state
        if (isOwnOAuthCallback && error === 'access_denied' && errorDescription) {
          toast.error(decodeURIComponent(errorDescription));
          navigate("/auth", { replace: true });
          setSession(null);
          setUser(null);
          return;
        }

        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          setSentryUser(currentSession.user.id);
          supabase
            .from('profiles')
            .select('platform_role')
            .eq('id', currentSession.user.id)
            .single()
            .then(({ data, error }) => {
              if (error) {
                console.error('Failed to fetch platform role:', error);
                // Leave platformRole as null — guard will block access until resolved
                return;
              }
              setPlatformRole(data?.platform_role ?? 'client');
            });
        } else {
          clearSentryUser();
          resetAnalytics();
          setPlatformRole(null);
        }

        if (event === "SIGNED_OUT") {
          if (!isPublicAuthRoute(locationPathnameRef.current)) {
            navigate("/auth", { replace: true });
          }
        }
        // SIGNED_IN / TOKEN_REFRESHED: no redirect here — Auth.tsx's `useEffect([user])`
        // and Index.tsx's `<Navigate>` already send the user to /overview as soon as
        // `user` is set. A delayed navigate here used to race those and could fire after
        // the user had already navigated elsewhere.
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(async ({ data: { session: currentSession } }) => {
      console.log("Initial session check:", { hasSession: !!currentSession });

      if (currentSession) {
        // Validate server-side — catches stale tokens after db reset or user deletion.
        // getSession() only reads localStorage; getUser() makes a real network call.
        const { error: userError } = await supabase.auth.getUser(currentSession.access_token);
        // Only a genuine "this session doesn't exist" answer means the session is
        // actually invalid. A retryable fetch error (network blip, transient
        // container-to-container hiccup — far more likely under load, e.g. CI, than
        // on a fast local run) is NOT proof the session is bad, and must not be
        // treated as one — see FIN-891: a spurious retryable error here used to fall
        // through to the same signOut() below, bouncing a consultant to /auth on a
        // plain reload for no real reason.
        if (userError && !isAuthRetryableFetchError(userError)) {
          console.log("Session invalid server-side, signing out:", userError.message);
          // Local scope only: this is this device's own session-health check, not a
          // user-initiated "log out everywhere." supabase-js's getUser(jwt) has
          // already locally cleared the session by this point when it detects a real
          // session_not_found (see its own internal _removeSession() call) — the
          // network signOut() below exists only to also invalidate this specific
          // session server-side. The SDK's default scope is 'global', which would
          // instead revoke every session this user has on every device — see FIN-891.
          await supabase.auth.signOut({ scope: 'local' });
          setSession(null);
          setUser(null);
          setPlatformRole(null);
          setLoading(false);
          if (!isPublicAuthRoute(locationPathnameRef.current)) {
            navigate("/auth", { replace: true });
          }
          return;
        }
      }

      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      if (currentSession?.user) {
        const { data, error } = await supabase
          .from('profiles')
          .select('platform_role')
          .eq('id', currentSession.user.id)
          .single();
        if (error) {
          console.error('Failed to fetch platform role:', error);
          // Leave platformRole as null — guard will block access until resolved
        } else {
          setPlatformRole((data?.platform_role as 'platform_admin' | 'consultant' | 'client') ?? 'client');
        }
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  // Both the sign-in listener and the session-restore path resolve platformRole asynchronously;
  // identifying here covers both, only once the role is actually known.
  const userId = user?.id;
  useEffect(() => {
    if (userId && platformRole) identifyUser(userId, platformRole);
  }, [userId, platformRole]);

  const signIn = async (email: string, password: string) => {
    const response = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return response;
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const response = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    
    return response;
  };

  const signInWithOAuth = async (provider: 'azure' | 'google') => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        scopes: 'email',
        redirectTo: redirectUrl,    
      },
    });

    if (error) {
      throw error;
    }
  };

  const signOut = async () => {
    // The open page's page_left needs the session it is sent with, so it goes before sign-out.
    await flushAnalytics();
    await supabase.auth.signOut();
    resetAnalytics();
  };

  const value = {
    user,
    session,
    loading,
    platformRole,
    signIn,
    signUp,
    signInWithOAuth,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
